import { useState, useEffect } from 'react';
import { checkWorkflowPermissions } from 'src/components/commonComponents/WorkflowTable/workflowPermissionUtils';
import { logger } from 'src/utils/logger';

type WorkflowPermissionAction = 'edit' | 'execute';

interface UseWorkflowPermissionResult {
	hasPermission: boolean;
	isCheckingPermission: boolean;
}

/**
 * Generic hook that checks whether the current user has a specific permission
 * (edit or execute) for a given workflow.
 *
 * @param workflowId - The workflow ID to check permissions for
 * @param userName   - The authenticated user's login name
 * @param action     - The permission action to check ('edit' or 'execute')
 */
export function useWorkflowPermission(
	workflowId: string | undefined,
	userName: string | undefined,
	action: WorkflowPermissionAction
): UseWorkflowPermissionResult {
	const [hasPermission, setHasPermission] = useState(false);
	const [isCheckingPermission, setIsCheckingPermission] = useState(!!workflowId && !!userName);

	useEffect(() => {
		let cancelled = false;

		async function verifyPermission() {
			if (!workflowId || !userName) {
				setHasPermission(false);
				// A cancelled in-flight check won't clear this, so clear it here
				setIsCheckingPermission(false);
				return;
			}

			setIsCheckingPermission(true);
			try {
				const authorizedIds = await checkWorkflowPermissions(
					[workflowId],
					userName,
					action
				);
				if (!cancelled) setHasPermission(authorizedIds.includes(workflowId));
			} catch (err) {
				logger.error(`Failed to check ${action} permissions [ workflowId : error ] ::: [ ${workflowId} : ${JSON.stringify(err)} ]`);
				if (!cancelled) setHasPermission(false);
			} finally {
				if (!cancelled) setIsCheckingPermission(false);
			}
		}

		verifyPermission();

		return () => {
			cancelled = true;
		};
	}, [workflowId, userName, action]);

	return { hasPermission, isCheckingPermission };
}
